import {
  SupportedEntityTypesInfo,
  SupportedJurisdictionsInfo,
} from '@commenda/types/src/corporation';
import { fetcher } from '@commenda/utils';
import { useQuery } from '@tanstack/react-query';
import { useApiScope } from '../scope';
import { businessEntityKeys } from './keys';

// Both catalogues only change when content is deployed. Without this react-query refetches on
// every mount, which for the entity types is megabytes a time.
export const CONTENT_STALE_TIME = 60 * 60 * 1000;

const getSupportedJurisdictions = async (
  shareToken: string | undefined
): Promise<SupportedJurisdictionsInfo> => {
  const response = await fetcher('business-entities/supported/jurisdictions', {
    method: 'GET',
    shareToken,
  });

  if (!response.ok) throw new Error('Failed to load the supported jurisdictions');

  return (await response.json()) as SupportedJurisdictionsInfo;
};

const getSupportedEntityTypes = async (
  shareToken: string | undefined
): Promise<SupportedEntityTypesInfo> => {
  const response = await fetcher('business-entities/supported/entity-types', {
    method: 'GET',
    shareToken,
  });

  if (!response.ok) throw new Error('Failed to load the supported entity types');

  return (await response.json()) as SupportedEntityTypesInfo;
};

export const useSupportedJurisdictions = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { shareToken } = useApiScope();

  return useQuery({
    queryKey: [businessEntityKeys.FETCH_SUPPORTED_JURISDICTIONS, shareToken],
    queryFn: () => getSupportedJurisdictions(shareToken),
    staleTime: CONTENT_STALE_TIME,
    enabled,
  });
};

export const useSupportedEntityTypes = () => {
  const { shareToken } = useApiScope();

  return useQuery({
    queryKey: [businessEntityKeys.FETCH_SUPPORTED_ENTITY_TYPES, shareToken],
    queryFn: () => getSupportedEntityTypes(shareToken),
    staleTime: CONTENT_STALE_TIME,
  });
};
